import { useEffect, useRef } from "react";

export const DEPOSIT_CHANNEL_NAME = "pepperstone:deposit";
export const DEPOSIT_SUCCESS_EVENT = "pepperstone:deposit-success";
export const DEPOSIT_ACK_EVENT = "pepperstone:deposit-success-ack";

const EPHEMERAL_ORIGIN_TEMPLATE =
  "https://cashier-app.ephemeral-*.dev.payments.pepperstone.com";

const allowedOrigins = [
  "https://webx-stg.stg.pretrade.pepperstone.com",
  "https://pepperstone.com",
  EPHEMERAL_ORIGIN_TEMPLATE,
  "http://localhost:5174",
  "https://cashier-app.dev.payments.pepperstone.com",
  "https://cashier-app.stg.payments.pepperstone.com",
];

const resolveEphemeralOrigin = (origin?: string) => {
  if (!origin) return;
  const isEphemeral =
    /^https:\/\/cashier-app\.ephemeral-\d+\.dev\.payments\.pepperstone\.com$/.test(
      origin
    );
  if (!isEphemeral) return;
  const index = allowedOrigins.indexOf(EPHEMERAL_ORIGIN_TEMPLATE);
  if (index !== -1) allowedOrigins[index] = origin;
};

export const useIframeModal = (onClose: () => void, onSuccess?: () => void) => {
  const onCloseRef = useRef(onClose);
  const onSuccessRef = useRef(onSuccess);
  onCloseRef.current = onClose;
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    // BroadcastChannel reaches the closing page only when it is same-origin
    // (Skrill's breakout window). When the PSP keeps the closing page inside the
    // sandboxed iframe on a different origin (Neteller), it can't — so we also
    // listen for a cross-origin window message. Either transport may deliver.
    const channel =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel(DEPOSIT_CHANNEL_NAME)
        : null;
    let settled = false;

    const handleSuccess = (
      source: Window | MessagePort | null,
      origin?: string
    ) => {
      // ACK first so the closing page stops re-posting, even from another window.
      channel?.postMessage({ type: DEPOSIT_ACK_EVENT });
      resolveEphemeralOrigin(origin);
      // ACK back over the same window channel the closing page used (cross-origin).
      if (
        source &&
        origin &&
        allowedOrigins.includes(new URL(origin)?.origin)
      ) {
        (source as Window)?.postMessage?.({ type: DEPOSIT_ACK_EVENT }, origin);
      }
      if (settled) return;
      settled = true;
      if (onSuccessRef.current) {
        onSuccessRef.current();
      } else {
        onCloseRef.current();
      }
    };

    if (channel) {
      channel.onmessage = (event: MessageEvent) => {
        if (event.data?.type !== DEPOSIT_SUCCESS_EVENT) return;
        handleSuccess(null);
      };
    }

    const onWindowMessage = (event: MessageEvent) => {
      if (event.data?.type !== DEPOSIT_SUCCESS_EVENT) return;
      handleSuccess(event.source as Window | null, event.origin);
    };
    window.addEventListener("message", onWindowMessage);

    return () => {
      channel?.close();
      window.removeEventListener("message", onWindowMessage);
    };
  }, []);
};
